// OAuth Service for Gmail and Outlook Email Integration
// Handles authorization flows, token storage, and refresh logic
//
// ⚠️ CRITICAL: This implements CUSTOM OAuth flows using redirect URIs
// - NOT using Blink SDK authentication (that's only for app login)
// - Gmail: Google OAuth 2.0 API with redirect to /oauth/callback
// - Outlook: Microsoft Azure AD OAuth 2.0 with redirect to /oauth/callback
// - State parameter saved in localStorage for CSRF protection
// - Tokens exchanged and stored securely via backend edge functions
//
// Flow:
// 1. initiateGmailOAuth/initiateOutlookOAuth - Opens provider auth page
// 2. User authorizes - Provider redirects to /oauth/callback with code
// 3. OAuthCallback.tsx - Exchanges code for tokens via backend
// 4. Tokens saved to email_accounts table in database
// 5. Success message sent to parent window via postMessage

// Determine if we're in production or local development
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

// OAuth redirect URI - use current origin for deployed apps, localhost:8001 for local dev
const REDIRECT_URI = isProduction 
  ? `${window.location.origin}/oauth/callback`
  : `http://localhost:8001/oauth/callback`;

// Backend API endpoints - use deployed Blink functions for production
const OAUTH_EXCHANGE_ENDPOINT = isProduction
  ? `https://ben3ou87--oauth-exchange.functions.blink.new`
  : 'http://localhost:8000/oauth-exchange';

const OAUTH_REFRESH_ENDPOINT = isProduction
  ? `https://ben3ou87--oauth-refresh.functions.blink.new`
  : 'http://localhost:8000/oauth-refresh';

const OAUTH_CONFIG_ENDPOINT = isProduction
  ? `https://ben3ou87--oauth-exchange.functions.blink.new/config`
  : 'http://localhost:8000/oauth-exchange/config';

const OAUTH_STATE_STORE_ENDPOINT = isProduction
  ? `https://ben3ou87--oauth-state-store.functions.blink.new`
  : 'http://localhost:8000/oauth-state-store';

// Scopes for Gmail API
const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/gmail.modify'
].join(' ');

// Scopes for Microsoft Graph API (Outlook)
const OUTLOOK_SCOPES = [
  'https://graph.microsoft.com/Mail.Read',
  'https://graph.microsoft.com/Mail.Send',
  'https://graph.microsoft.com/Mail.ReadWrite'
].join(' ');

export interface OAuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  provider: 'gmail' | 'outlook';
  email?: string;
}

// Import Blink client for database operations
import { blink } from '@/lib/blink';

/**
 * Store OAuth tokens in database (server-side)
 */
async function storeTokens(userId: string, provider: 'gmail' | 'outlook', tokens: OAuthTokens): Promise<void> {
  try {
    // Call backend function to save tokens securely
    const response = await fetch('https://ben3ou87--save-oauth-tokens.functions.blink.new', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        provider,
        email: tokens.email || '',
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: tokens.expiresAt
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to store tokens');
    }
    
    // Invalidate caches when tokens are updated
    requestManager.clearCache(`oauth:tokens:${userId}:${provider}`);
    requestManager.clearCache(`oauth:connection-status:${userId}`);
    requestManager.clearCache(`account-sync-status:${userId}`);
    console.log(`🔄 Cleared token caches for ${provider}`);
  } catch (error) {
    console.error('Failed to store tokens:', error);
    throw error;
  }
}

// Import request manager
import { requestManager } from '@/lib/requestManager';

/**
 * Retrieve OAuth tokens from database (server-side) with caching
 */
async function getStoredTokens(userId: string, provider: 'gmail' | 'outlook'): Promise<OAuthTokens | null> {
  try {
    const accounts = await requestManager.execute(
      `oauth:tokens:${userId}:${provider}`,
      () => blink.db.emailAccounts.list({
        where: { userId, provider },
        limit: 1
      }),
      {
        priority: 8,
        cacheTTL: 30000
      }
    );

    if (!accounts || accounts.length === 0) {
      console.log(`⚠️ No ${provider} account found for user ${userId}`);
      return null;
    }

    const account = accounts[0];
    console.log(`✅ Found ${provider} account from DB (cached for 30s)`);
    
    // Validate required fields
    if (!account.accessToken) {
      console.error(`❌ No access token for ${provider} account`);
      return null;
    }
    
    if (!account.tokenExpiresAt) {
      console.error(`❌ No token expiration for ${provider} account`);
      return null;
    }
    
    return {
      accessToken: account.accessToken,
      refreshToken: account.refreshToken || undefined,
      expiresAt: new Date(account.tokenExpiresAt).getTime(),
      provider,
      email: account.emailAddress
    } as OAuthTokens;
  } catch (error) {
    console.error('Failed to get stored tokens:', error);
    return null;
  }
}

/**
 * Clear OAuth tokens from database (server-side)
 */
export async function clearTokens(userId: string, provider: 'gmail' | 'outlook'): Promise<void> {
  try {
    const accounts = await blink.db.emailAccounts.list({
      where: { userId, provider }
    });

    for (const account of accounts) {
      await blink.db.emailAccounts.delete(account.id);
    }
    
    // Invalidate caches when tokens are cleared
    requestManager.clearCache(`oauth:tokens:${userId}:${provider}`);
    requestManager.clearCache(`oauth:connection-status:${userId}`);
    requestManager.clearCache(`account-sync-status:${userId}`);
    console.log(`🔄 Cleared token caches for ${provider} (disconnected)`);
  } catch (error) {
    console.error('Failed to clear tokens:', error);
  }
}

/**
 * Check if access token is expired
 */
function isTokenExpired(expiresAt: number): boolean {
  // Add 5 minute buffer
  return Date.now() >= expiresAt - 5 * 60 * 1000;
}

/**
 * Generate random state parameter for OAuth security
 */
function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Save OAuth state to server (fallback when localStorage fails)
 */
async function saveStateToServer(state: string, provider: 'gmail' | 'outlook', userId: string): Promise<boolean> {
  try {
    const response = await fetch(OAUTH_STATE_STORE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        state,
        provider,
        userId,
        timestamp: Date.now()
      })
    });

    if (!response.ok) {
      console.error('Failed to save state to server:', response.status);
      return false;
    }

    const data = await response.json();
    console.log('✅ OAuth state saved to server:', data.stateId);
    return true;
  } catch (error) {
    console.error('Error saving state to server:', error);
    return false;
  }
}

/**
 * Retrieve OAuth state from server
 */
async function getStateFromServer(state: string): Promise<{ provider: string; userId: string; timestamp: number } | null> {
  try {
    const response = await fetch(`${OAUTH_STATE_STORE_ENDPOINT}?state=${encodeURIComponent(state)}`);

    if (!response.ok) {
      console.error('Failed to get state from server:', response.status);
      return null;
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Error getting state from server:', error);
    return null;
  }
}

/**
 * Clear OAuth state from server after use
 */
async function clearStateFromServer(state: string): Promise<void> {
  try {
    await fetch(`${OAUTH_STATE_STORE_ENDPOINT}?state=${encodeURIComponent(state)}`, {
      method: 'DELETE'
    });
  } catch (error) {
    console.error('Error clearing state from server:', error);
  }
}

/**
 * Check if OAuth credentials are configured
 * Since OAuth is handled on the backend, we always return true
 * The backend will handle any configuration errors
 */
export function isOAuthConfigured(provider: 'gmail' | 'outlook'): boolean {
  // OAuth configuration is managed on the backend via edge functions
  // Always show connect buttons - backend will handle validation
  return true;
}

/**
 * Initiate Gmail OAuth flow
 * Opens Google's authorization page in a popup window
 */
export async function initiateGmailOAuth(): Promise<OAuthTokens> {
  return new Promise(async (resolve, reject) => {
    try {
      // 🔧 CRITICAL: Get current user ID BEFORE opening popup
      // This prevents auth state interference in the callback popup
      let currentUserId: string;
      try {
        const user = await blink.auth.me();
        if (!user) {
          reject(new Error('Please sign in to your account before connecting email'));
          return;
        }
        currentUserId = user.id;
        console.log(`🔐 Current user ID: ${currentUserId}`);
      } catch (error) {
        console.error('❌ Failed to get current user:', error);
        reject(new Error('Authentication required - please sign in first'));
        return;
      }
      
      // Clear any stale OAuth state first to prevent conflicts
      const existingState = localStorage.getItem('oauth_state');
      const existingTimestamp = localStorage.getItem('oauth_state_timestamp');
      
      if (existingState && existingTimestamp) {
        const age = Date.now() - parseInt(existingTimestamp);
        if (age > 10 * 60 * 1000) { // Older than 10 minutes
          console.log('🧹 Clearing stale OAuth state (age: ' + Math.round(age / 1000) + 's)');
          localStorage.removeItem('oauth_state');
          localStorage.removeItem('oauth_provider');
          localStorage.removeItem('oauth_state_timestamp');
          localStorage.removeItem('oauth_user_id');
        }
      }
      
      // Log environment for debugging
      console.log('🔐 Initiating Gmail OAuth...');
      console.log('  Environment:', isProduction ? 'Production' : 'Local Development');
      console.log('  Config endpoint:', `${OAUTH_CONFIG_ENDPOINT}?provider=gmail`);
      
      // Get OAuth config from backend with retry
      let configResponse;
      let retries = 3;
      let lastError;
      
      while (retries > 0) {
        try {
          configResponse = await fetch(`${OAUTH_CONFIG_ENDPOINT}?provider=gmail`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          });
          if (configResponse.ok) break;
          lastError = `HTTP ${configResponse.status}`;
        } catch (error) {
          lastError = error instanceof Error ? error.message : String(error);
          console.warn(`Config fetch attempt failed (${retries} retries left):`, lastError);
        }
        retries--;
        if (retries > 0) await new Promise(r => setTimeout(r, 1000));
      }
      
      if (!configResponse || !configResponse.ok) {
        console.error('❌ Failed to get Gmail OAuth configuration:', lastError);
        reject(new Error(`Failed to get OAuth configuration: ${lastError}`));
        return;
      }
      const config = await configResponse.json();
      console.log('✅ Gmail OAuth configuration retrieved successfully');
      
      const state = generateState();
      
      // 🔧 ENHANCED: Try localStorage first, fallback to server storage
      let useServerStorage = false;
      try {
        // Test localStorage access
        const testKey = '__oauth_test_' + Date.now();
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        
        // Use localStorage for cross-window reliability
        localStorage.setItem('oauth_state', state);
        localStorage.setItem('oauth_provider', 'gmail');
        localStorage.setItem('oauth_state_timestamp', Date.now().toString());
        localStorage.setItem('oauth_user_id', currentUserId);
        
        // Verify all values were saved
        const savedState = localStorage.getItem('oauth_state');
        const savedProvider = localStorage.getItem('oauth_provider');
        const savedUserId = localStorage.getItem('oauth_user_id');
        
        if (!savedState || !savedProvider || !savedUserId) {
          throw new Error('Failed to persist OAuth state - values not saved to localStorage');
        }
        
        console.log('🔐 Gmail OAuth State Saved to localStorage:');
        console.log('  State:', state.substring(0, 16) + '...');
        console.log('  Provider:', savedProvider);
        console.log('  User ID:', savedUserId);
        console.log('  Storage: localStorage ✅');
      } catch (localStorageError) {
        console.warn('⚠️  localStorage unavailable, using server storage fallback...');
        console.warn('   Reason:', localStorageError instanceof Error ? localStorageError.message : 'Unknown');
        
        // Fallback to server storage
        const serverSaveSuccess = await saveStateToServer(state, 'gmail', currentUserId);
        if (!serverSaveSuccess) {
          reject(new Error('Failed to save OAuth state - both localStorage and server storage failed. Please check browser settings.'));
          return;
        }
        
        useServerStorage = true;
        console.log('🔐 Gmail OAuth State Saved to server:');
        console.log('  State:', state.substring(0, 16) + '...');
        console.log('  Provider: gmail');
        console.log('  User ID:', currentUserId);
        console.log('  Storage: Server-side ✅');
      }
      
      // Mark whether we're using server storage
      try {
        sessionStorage.setItem('oauth_use_server_storage', useServerStorage ? '1' : '0');
      } catch {
        // If sessionStorage also fails, OAuth callback will try both methods
      }
      console.log('');

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', config.clientId);
      authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', GMAIL_SCOPES);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      
      console.log('🔐 Gmail OAuth Configuration:');
      console.log('  Client ID:', config.clientId);
      console.log('  Redirect URI:', REDIRECT_URI);
      console.log('  Scopes:', GMAIL_SCOPES);
      console.log('');
      console.log('✅ IMPORTANT: You should see YOUR app name on the consent screen, NOT "blink.new"');
      console.log('   If you see "blink.new", you need to set up your own Google OAuth client.');
      console.log('   See CUSTOM_OAUTH_SETUP_GUIDE.md for instructions.');
      console.log('');

      // Open popup window
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl.toString(),
        'Gmail OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        reject(new Error('Failed to open OAuth popup. Please allow popups for this site.'));
        return;
      }

      // Track if OAuth has completed (success or error)
      let hasResolved = false;
      
      // Listen for OAuth callback
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'oauth_success' && event.data.provider === 'gmail') {
          hasResolved = true;
          clearTimeout(timeoutId);
          window.removeEventListener('message', messageHandler);
          window.removeEventListener('message', popupCloseHandler);
          
          // Send acknowledgment back to popup
          console.log('Sending acknowledgment to popup');
          try {
            if (popup && !popup.closed) {
              popup.postMessage({
                type: 'oauth_ack',
                provider: 'gmail'
              }, window.location.origin);
            }
          } catch (e) {
            // Ignore COOP errors when sending ack - popup may already be closing
            console.log('Could not send ack to popup (likely already closed)');
          }
          
          resolve(event.data.tokens);
        } else if (event.data.type === 'oauth_error') {
          hasResolved = true;
          clearTimeout(timeoutId);
          window.removeEventListener('message', messageHandler);
          window.removeEventListener('message', popupCloseHandler);
          try {
            if (popup && !popup.closed) {
              popup.close();
            }
          } catch (e) {
            // Ignore COOP errors
          }
          reject(new Error(event.data.error || 'OAuth failed'));
        }
      };

      window.addEventListener('message', messageHandler);

      // Set a timeout instead of polling window.closed
      // This avoids COOP errors while still handling abandoned OAuth flows
      const timeoutId = setTimeout(() => {
        if (!hasResolved) {
          window.removeEventListener('message', messageHandler);
          window.removeEventListener('message', popupCloseHandler);
          // Clear OAuth state
          localStorage.removeItem('oauth_state');
          localStorage.removeItem('oauth_provider');
          localStorage.removeItem('oauth_state_timestamp');
          localStorage.removeItem('oauth_user_id');
          reject(new Error('OAuth flow timed out after 5 minutes. Please try again.'));
        }
      }, 5 * 60 * 1000); // 5 minute timeout

      // Listen for popup close event via message (sent by popup's beforeunload)
      const popupCloseHandler = (event: MessageEvent) => {
        if (event.origin === window.location.origin && event.data.type === 'oauth_popup_closed') {
          setTimeout(() => {
            if (!hasResolved) {
              clearTimeout(timeoutId);
              window.removeEventListener('message', messageHandler);
              window.removeEventListener('message', popupCloseHandler);
              // Clear OAuth state
              localStorage.removeItem('oauth_state');
              localStorage.removeItem('oauth_provider');
              localStorage.removeItem('oauth_state_timestamp');
              localStorage.removeItem('oauth_user_id');
              reject(new Error('OAuth popup was closed before completing authentication'));
            }
          }, 1000); // Small delay to allow success message to arrive
        }
      };
      window.addEventListener('message', popupCloseHandler);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Initiate Outlook OAuth flow
 * Opens Microsoft's authorization page in a popup window
 */
export async function initiateOutlookOAuth(): Promise<OAuthTokens> {
  return new Promise(async (resolve, reject) => {
    try {
      // 🔧 CRITICAL: Get current user ID BEFORE opening popup
      // This prevents auth state interference in the callback popup
      let currentUserId: string;
      try {
        const user = await blink.auth.me();
        if (!user) {
          reject(new Error('Please sign in to your account before connecting email'));
          return;
        }
        currentUserId = user.id;
        console.log(`🔐 Current user ID: ${currentUserId}`);
      } catch (error) {
        console.error('❌ Failed to get current user:', error);
        reject(new Error('Authentication required - please sign in first'));
        return;
      }
      
      // Clear any stale OAuth state first to prevent conflicts
      const existingState = localStorage.getItem('oauth_state');
      const existingTimestamp = localStorage.getItem('oauth_state_timestamp');
      
      if (existingState && existingTimestamp) {
        const age = Date.now() - parseInt(existingTimestamp);
        if (age > 10 * 60 * 1000) { // Older than 10 minutes
          console.log('🧹 Clearing stale OAuth state (age: ' + Math.round(age / 1000) + 's)');
          localStorage.removeItem('oauth_state');
          localStorage.removeItem('oauth_provider');
          localStorage.removeItem('oauth_state_timestamp');
          localStorage.removeItem('oauth_user_id');
        }
      }
      
      // Log environment for debugging
      console.log('🔐 Initiating Outlook OAuth...');
      console.log('  Environment:', isProduction ? 'Production' : 'Local Development');
      console.log('  Config endpoint:', `${OAUTH_CONFIG_ENDPOINT}?provider=outlook`);
      
      // Get OAuth config from backend with retry
      let configResponse;
      let retries = 3;
      let lastError;
      
      while (retries > 0) {
        try {
          configResponse = await fetch(`${OAUTH_CONFIG_ENDPOINT}?provider=outlook`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          });
          if (configResponse.ok) break;
          lastError = `HTTP ${configResponse.status}`;
        } catch (error) {
          lastError = error instanceof Error ? error.message : String(error);
          console.warn(`Config fetch attempt failed (${retries} retries left):`, lastError);
        }
        retries--;
        if (retries > 0) await new Promise(r => setTimeout(r, 1000));
      }
      
      if (!configResponse || !configResponse.ok) {
        console.error('❌ Failed to get Outlook OAuth configuration:', lastError);
        reject(new Error(`Failed to get OAuth configuration: ${lastError}`));
        return;
      }
      const config = await configResponse.json();
      console.log('✅ Outlook OAuth configuration retrieved successfully');
      
      const state = generateState();
      
      // 🔧 ENHANCED: Try localStorage first, fallback to server storage
      let useServerStorage = false;
      try {
        // Test localStorage access
        const testKey = '__oauth_test_' + Date.now();
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        
        // Use localStorage for cross-window reliability
        localStorage.setItem('oauth_state', state);
        localStorage.setItem('oauth_provider', 'outlook');
        localStorage.setItem('oauth_state_timestamp', Date.now().toString());
        localStorage.setItem('oauth_user_id', currentUserId);
        
        // Verify all values were saved
        const savedState = localStorage.getItem('oauth_state');
        const savedProvider = localStorage.getItem('oauth_provider');
        const savedUserId = localStorage.getItem('oauth_user_id');
        
        if (!savedState || !savedProvider || !savedUserId) {
          throw new Error('Failed to persist OAuth state - values not saved to localStorage');
        }
        
        console.log('🔐 Outlook OAuth State Saved to localStorage:');
        console.log('  State:', state.substring(0, 16) + '...');
        console.log('  Provider:', savedProvider);
        console.log('  User ID:', savedUserId);
        console.log('  Storage: localStorage ✅');
      } catch (localStorageError) {
        console.warn('⚠️  localStorage unavailable, using server storage fallback...');
        console.warn('   Reason:', localStorageError instanceof Error ? localStorageError.message : 'Unknown');
        
        // Fallback to server storage
        const serverSaveSuccess = await saveStateToServer(state, 'outlook', currentUserId);
        if (!serverSaveSuccess) {
          reject(new Error('Failed to save OAuth state - both localStorage and server storage failed. Please check browser settings.'));
          return;
        }
        
        useServerStorage = true;
        console.log('🔐 Outlook OAuth State Saved to server:');
        console.log('  State:', state.substring(0, 16) + '...');
        console.log('  Provider: outlook');
        console.log('  User ID:', currentUserId);
        console.log('  Storage: Server-side ✅');
      }
      
      // Mark whether we're using server storage
      try {
        sessionStorage.setItem('oauth_use_server_storage', useServerStorage ? '1' : '0');
      } catch {
        // If sessionStorage also fails, OAuth callback will try both methods
      }
      console.log('');
      console.log('🔐 Azure OAuth Configuration:');
      console.log('  Client ID:', config.clientId);
      console.log('  Redirect URI:', REDIRECT_URI);
      console.log('  Scopes:', OUTLOOK_SCOPES);
      console.log('');
      console.log('⚠️  CRITICAL: Ensure this EXACT redirect URI is registered in Azure Portal:');
      console.log('  → ' + REDIRECT_URI);
      console.log('');
      console.log('📍 Azure Portal: https://portal.azure.com');
      console.log('   Navigate to: App Registrations → Your App → Authentication → Redirect URIs');

      const authUrl = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
      authUrl.searchParams.set('client_id', config.clientId);
      authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', OUTLOOK_SCOPES);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('response_mode', 'query');

      // Open popup window
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl.toString(),
        'Outlook OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        reject(new Error('Failed to open OAuth popup. Please allow popups for this site.'));
        return;
      }

      // Track if OAuth has completed (success or error)
      let hasResolved = false;
      
      // Listen for OAuth callback
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'oauth_success' && event.data.provider === 'outlook') {
          hasResolved = true;
          clearTimeout(timeoutId);
          window.removeEventListener('message', messageHandler);
          window.removeEventListener('message', popupCloseHandler);
          
          // Send acknowledgment back to popup
          console.log('Sending acknowledgment to popup');
          try {
            if (popup && !popup.closed) {
              popup.postMessage({
                type: 'oauth_ack',
                provider: 'outlook'
              }, window.location.origin);
            }
          } catch (e) {
            // Ignore COOP errors when sending ack - popup may already be closing
            console.log('Could not send ack to popup (likely already closed)');
          }
          
          resolve(event.data.tokens);
        } else if (event.data.type === 'oauth_error') {
          hasResolved = true;
          clearTimeout(timeoutId);
          window.removeEventListener('message', messageHandler);
          window.removeEventListener('message', popupCloseHandler);
          try {
            if (popup && !popup.closed) {
              popup.close();
            }
          } catch (e) {
            // Ignore COOP errors
          }
          reject(new Error(event.data.error || 'OAuth failed'));
        }
      };

      window.addEventListener('message', messageHandler);

      // Set a timeout instead of polling window.closed
      // This avoids COOP errors while still handling abandoned OAuth flows
      const timeoutId = setTimeout(() => {
        if (!hasResolved) {
          window.removeEventListener('message', messageHandler);
          window.removeEventListener('message', popupCloseHandler);
          // Clear OAuth state
          localStorage.removeItem('oauth_state');
          localStorage.removeItem('oauth_provider');
          localStorage.removeItem('oauth_state_timestamp');
          localStorage.removeItem('oauth_user_id');
          reject(new Error('OAuth flow timed out after 5 minutes. Please try again.'));
        }
      }, 5 * 60 * 1000); // 5 minute timeout

      // Listen for popup close event via message (sent by popup's beforeunload)
      const popupCloseHandler = (event: MessageEvent) => {
        if (event.origin === window.location.origin && event.data.type === 'oauth_popup_closed') {
          setTimeout(() => {
            if (!hasResolved) {
              clearTimeout(timeoutId);
              window.removeEventListener('message', messageHandler);
              window.removeEventListener('message', popupCloseHandler);
              // Clear OAuth state
              localStorage.removeItem('oauth_state');
              localStorage.removeItem('oauth_provider');
              localStorage.removeItem('oauth_state_timestamp');
              localStorage.removeItem('oauth_user_id');
              reject(new Error('OAuth popup was closed before completing authentication'));
            }
          }, 1000); // Small delay to allow success message to arrive
        }
      };
      window.addEventListener('message', popupCloseHandler);
    } catch (error) {
      // Don't clear OAuth state on error during initiation
      // The callback will handle clearing if needed
      console.error('Outlook OAuth initiation error:', error);
      reject(error);
    }
  });
}

/**
 * Get valid access token, refreshing if necessary
 * Emits events for UI notification on errors
 */
export async function getValidAccessToken(provider: 'gmail' | 'outlook', userId?: string): Promise<string | null> {
  // Get userId from Blink auth if not provided
  if (!userId) {
    try {
      const user = await blink.auth.me();
      userId = user.id;
    } catch (error) {
      console.error('Failed to get user:', error);
      return null;
    }
  }

  const tokens = await getStoredTokens(userId, provider);
  
  if (!tokens) {
    return null;
  }

  // If token is still valid, return it
  if (!isTokenExpired(tokens.expiresAt)) {
    return tokens.accessToken;
  }

  // Token expired, try to refresh
  if (tokens.refreshToken) {
    try {
      const refreshed = await refreshAccessToken(userId, provider, tokens.refreshToken);
      return refreshed.accessToken;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      await clearTokens(userId, provider);
      
      // Emit event for UI notification
      window.dispatchEvent(new CustomEvent('oauth-refresh-failed', {
        detail: {
          provider,
          error: error instanceof Error ? error.message : 'Token refresh failed'
        }
      }));
      
      return null;
    }
  }

  // No refresh token available
  await clearTokens(userId, provider);
  
  // Emit event for UI notification
  window.dispatchEvent(new CustomEvent('oauth-token-expired', {
    detail: {
      provider,
      message: 'Please reconnect your account'
    }
  }));
  
  return null;
}

/**
 * Refresh access token using refresh token with retry/backoff logic
 * Uses secure backend endpoint to keep client secrets safe
 */
async function refreshAccessToken(userId: string, provider: 'gmail' | 'outlook', refreshToken: string): Promise<OAuthTokens> {
  const maxRetries = 3;
  let lastError: any = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Token refresh attempt ${attempt}/${maxRetries} for ${provider}...`);

      const response = await fetch(OAUTH_REFRESH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider,
          refreshToken,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        const errorMsg = error.error || 'Failed to refresh token';
        
        // Check if error is retryable
        if (errorMsg.includes('invalid') || errorMsg.includes('revoked')) {
          console.error('Non-retryable token refresh error:', errorMsg);
          throw new Error(errorMsg);
        }
        
        throw new Error(errorMsg);
      }

      const data = await response.json();
      const tokens: OAuthTokens = {
        accessToken: data.tokens.accessToken,
        refreshToken: data.tokens.refreshToken || refreshToken,
        expiresAt: data.tokens.expiresAt,
        provider,
        email: data.tokens.email
      };

      await storeTokens(userId, provider, tokens);
      console.log(`✅ Token refreshed successfully for ${provider}`);
      return tokens;
    } catch (error) {
      lastError = error;
      console.error(`Token refresh attempt ${attempt} failed:`, error);

      // If non-retryable, break immediately
      const msg = (error && error instanceof Error) ? error.message.toLowerCase() : '';
      if (msg.includes('invalid') || msg.includes('revoked')) {
        break;
      }

      // Exponential backoff: 1s, 2s, 4s
      if (attempt < maxRetries) {
        const delayMs = Math.pow(2, attempt - 1) * 1000;
        console.log(`Waiting ${delayMs}ms before retry...`);
        await new Promise((res) => setTimeout(res, delayMs));
      }
    }
  }

  // All retries failed
  console.error(`❌ Token refresh failed after ${maxRetries} attempts`);
  if (lastError) {
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }
  throw new Error('Token refresh failed after multiple attempts');
}

/**
 * Check if user is connected to a provider
 */
export async function isConnected(userId: string, provider: 'gmail' | 'outlook'): Promise<boolean> {
  const tokens = await getStoredTokens(userId, provider);
  return tokens !== null;
}

/**
 * Get connection status for both providers with caching
 */
export async function getConnectionStatus(userId: string): Promise<{
  gmail: boolean;
  outlook: boolean;
}> {
  // Request manager will handle caching internally for the isConnected calls
  const [gmail, outlook] = await Promise.all([
    isConnected(userId, 'gmail'),
    isConnected(userId, 'outlook')
  ]);
  
  return { gmail, outlook };
}

/**
 * Disconnect from a provider (clear tokens)
 */
export async function disconnect(userId: string, provider: 'gmail' | 'outlook'): Promise<void> {
  await clearTokens(userId, provider);
}

/**
 * Store tokens after OAuth callback
 * Called from the OAuth callback page
 */
export async function handleOAuthCallback(
  userId: string,
  provider: 'gmail' | 'outlook',
  tokens: OAuthTokens
): Promise<void> {
  await storeTokens(userId, provider, tokens);
  
  // Send message to opener window
  if (window.opener) {
    window.opener.postMessage({
      type: 'oauth_success',
      provider,
      tokens
    }, window.location.origin);
  }
}

/**
 * Handle OAuth error
 * Called from the OAuth callback page
 */
export function handleOAuthError(error: string): void {
  if (window.opener) {
    window.opener.postMessage({
      type: 'oauth_error',
      error
    }, window.location.origin);
  }
}